import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';

interface Environment3DPropertiesProps {
    object: {
        buildings?: any[];
        parcels?: any[];
        shape?: any[];
    };
}

export const Environment3DPropertiesSimple: React.FC<Environment3DPropertiesProps> = ({ object }) => {
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);

    useEffect(() => {
        if (!mapContainerRef.current) return;

        console.log('🏢 [3D SIMPLE] Starting simplified 3D implementation');

        // Initialize Mapbox
        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: 'mapbox://styles/mapbox/light-v11',
            center: [object.shape?.[0]?.lon || 8.669347, object.shape?.[0]?.lat || 50.106277],
            zoom: 18,
            pitch: 45,
            bearing: 0,
            antialias: true
        });

        mapRef.current = map;

        map.on('load', async () => {
            console.log('🏢 [3D SIMPLE] Map loaded, using Mapbox fill-extrusion only...');

            // Test shapes commented out - 3D rendering is now working correctly
            /*
            // Add a simple test shape to verify Mapbox fill-extrusion is working
            const testGeoJSON = {
                type: 'FeatureCollection' as const,
                features: [{
                    type: 'Feature' as const,
                    geometry: {
                        type: 'Polygon' as const,
                        coordinates: [[
                            [8.669347, 50.106277],
                            [8.669447, 50.106277],
                            [8.669447, 50.106377],
                            [8.669347, 50.106377],
                            [8.669347, 50.106277]
                        ]]
                    },
                    properties: {
                        height: 20,
                        color: '#ff0000'
                    }
                }]
            };
            
            map.addSource('test-extrusion', {
                type: 'geojson',
                data: testGeoJSON
            });
            
            map.addLayer({
                id: 'test-extrusion-layer',
                type: 'fill-extrusion',
                source: 'test-extrusion',
                paint: {
                    'fill-extrusion-color': ['get', 'color'],
                    'fill-extrusion-height': ['get', 'height'],
                    'fill-extrusion-base': 0,
                    'fill-extrusion-opacity': 0.9
                }
            });
            
            console.log('🏢 [3D TEST] Added red test cube to verify Mapbox fill-extrusion is working');
            */

            // Debug the object data
            console.log('🏢 [3D DEBUG] Object data received:', {
                hasBuildings: !!object.buildings,
                buildingsCount: object.buildings?.length || 0,
                hasParcels: !!object.parcels,
                parcelsCount: object.parcels?.length || 0,
                hasShape: !!object.shape,
                shapeCount: object.shape?.length || 0,
                fullObject: object
            });

            // CLIENT-SIDE RENDERING: Extrude existing parcel and building shapes
            console.log('🏢 [3D CLIENT] Rendering existing parcel and building shapes on client side');
            
            // Green hexagon test shape commented out - 3D rendering is now working correctly
            /*
            // Add a complex polygon test at the specific coordinates you mentioned
            const specificTestGeoJSON = {
                type: 'FeatureCollection' as const,
                features: [{
                    type: 'Feature' as const,
                    geometry: {
                        type: 'Polygon' as const,
                        coordinates: [[
                            // Create an irregular hexagon shape around the coordinates
                            [8.863175, 50.206345], // Center point
                            [8.863275, 50.206345], // East
                            [8.863325, 50.206395], // Northeast
                            [8.863275, 50.206445], // North
                            [8.863175, 50.206445], // Northwest
                            [8.863125, 50.206395], // Southwest
                            [8.863075, 50.206345], // South
                            [8.863175, 50.206345]  // Close the polygon
                        ]]
                    },
                    properties: {
                        height: 25,
                        color: '#00ff00'
                    }
                }]
            };
            
            // Add the specific test GeoJSON source
            map.addSource('specific-test-extrusion', {
                type: 'geojson',
                data: specificTestGeoJSON
            });
            
            // Add the specific test fill-extrusion layer
            map.addLayer({
                id: 'specific-test-extrusion-layer',
                type: 'fill-extrusion',
                source: 'specific-test-extrusion',
                paint: {
                    'fill-extrusion-color': ['get', 'color'],
                    'fill-extrusion-height': ['get', 'height'],
                    'fill-extrusion-base': 0,
                    'fill-extrusion-opacity': 0.9
                }
            });
            
            console.log('🏢 [3D TEST] Added green test extrusion at specific coordinates (50.206345, 8.863175)');
            */
            
            // Orange L-shaped test shape commented out - 3D rendering is now working correctly
            /*
            // Add another complex polygon test - an L-shaped building
            const lShapeTestGeoJSON = {
                type: 'FeatureCollection' as const,
                features: [{
                    type: 'Feature' as const,
                    geometry: {
                        type: 'Polygon' as const,
                        coordinates: [[
                            // Create an L-shaped polygon at a different location (southwest of green shape)
                            [8.863075, 50.206245], // Southwest corner
                            [8.863125, 50.206245], // East
                            [8.863125, 50.206275], // Northeast
                            [8.863100, 50.206275], // North
                            [8.863100, 50.206295], // North
                            [8.863075, 50.206295], // Northwest
                            [8.863075, 50.206275], // West
                            [8.863075, 50.206245]  // Close the polygon
                        ]]
                    },
                    properties: {
                        height: 15,
                        color: '#ff8800'
                    }
                }]
            };
            
            // Add the L-shaped test GeoJSON source
            map.addSource('lshape-test-extrusion', {
                type: 'geojson',
                data: lShapeTestGeoJSON
            });
            
            // Add the L-shaped test fill-extrusion layer
            map.addLayer({
                id: 'lshape-test-extrusion-layer',
                type: 'fill-extrusion',
                source: 'lshape-test-extrusion',
                paint: {
                    'fill-extrusion-color': ['get', 'color'],
                    'fill-extrusion-height': ['get', 'height'],
                    'fill-extrusion-base': 0,
                    'fill-extrusion-opacity': 0.9
                }
            });
            
            console.log('🏢 [3D TEST] Added orange L-shaped test extrusion');
            */
            
            // Get center coordinates from shape data
            const centerLat = object.shape?.[0]?.lat || 50.106277;
            const centerLon = object.shape?.[0]?.lon || 8.669347;
            
            console.log('🏢 [3D CLIENT] Processing LoD2 data:', {
                hasBuildings: !!object.buildings,
                buildingsCount: object.buildings?.length || 0,
                hasParcels: !!object.parcels,
                parcelsCount: object.parcels?.length || 0,
                centerLat,
                centerLon
            });

            // Process buildings using Mapbox fill-extrusion
            if (object.buildings && object.buildings.length > 0) {
                console.log('🏢 [3D CLIENT] Processing buildings with Mapbox fill-extrusion...');
                
                // Create GeoJSON for all buildings
                const buildingsGeoJSON = {
                    type: 'FeatureCollection' as const,
                    features: object.buildings
                        .map((building: any, index: number) => {
                            console.log('🏢 [3D CLIENT] Processing building:', {
                                index,
                                name: building.name,
                                hasGeometry: !!building.geometry,
                                geometryType: building.geometry?.type
                            });
                            
                        if (building.geometry && building.geometry.coordinates) {
                            const height = building.height || 10; // Default height
                            
                            console.log('🏢 [3D CLIENT] Building geometry:', {
                                coordinates: building.geometry.coordinates,
                                height: height,
                                fullBuilding: JSON.stringify(building, null, 2)
                            });
                            
                            // Validate coordinates - check if they're reasonable
                            const coords = building.geometry.coordinates;
                            if (coords && coords[0] && Array.isArray(coords[0])) {
                                const firstRing = coords[0];
                                const validCoords = firstRing.filter((coord: any) => {
                                    if (Array.isArray(coord) && coord.length >= 2) {
                                        const [lon, lat] = coord;
                                        // Check if coordinates are reasonable (within a reasonable range)
                                        const isReasonable = lon > 8.8 && lon < 8.9 && lat > 50.1 && lat < 50.3;
                                        if (!isReasonable) {
                                            console.warn('🏢 [3D CLIENT] Invalid coordinate detected:', coord, 'lon:', lon, 'lat:', lat);
                                        }
                                        return isReasonable;
                                    }
                                    return false;
                                });
                                
                                if (validCoords.length < 3) {
                                    console.warn('🏢 [3D CLIENT] Building has too few valid coordinates:', validCoords.length);
                                    return null;
                                }
                                
                                console.log('🏢 [3D CLIENT] Building has', validCoords.length, 'valid coordinates out of', firstRing.length);
                                
                                // Ensure polygon is properly closed
                                const closedCoords = [...validCoords];
                                if (closedCoords.length > 0) {
                                    const first = closedCoords[0];
                                    const last = closedCoords[closedCoords.length - 1];
                                    if (first[0] !== last[0] || first[1] !== last[1]) {
                                        closedCoords.push([first[0], first[1]]);
                                        console.log('🏢 [3D CLIENT] Closed polygon for building', index);
                                    }
                                }
                                
                                // Use light blue for all buildings
                                const color = '#87CEEB'; // Light blue
                                
                                return {
                                    type: 'Feature' as const,
                                    geometry: {
                                        type: 'Polygon' as const,
                                        coordinates: [closedCoords] // Use closed coordinates
                                    },
                                    properties: {
                                        height: height,
                                        color: color,
                                        name: building.name || `Building ${index}`
                                    }
                                };
                            }
                        }
                        return null;
                        })
                        .filter((feature): feature is NonNullable<typeof feature> => feature !== null)
                };
                
                // Add buildings source and layer
                if (buildingsGeoJSON.features.length > 0) {
                    map.addSource('buildings-extrusion', {
                        type: 'geojson',
                        data: buildingsGeoJSON
                    });
                    
                    map.addLayer({
                        id: 'buildings-extrusion-layer',
                        type: 'fill-extrusion',
                        source: 'buildings-extrusion',
                        paint: {
                            'fill-extrusion-color': ['get', 'color'],
                            'fill-extrusion-height': ['get', 'height'],
                            'fill-extrusion-base': 0,
                            'fill-extrusion-opacity': 0.9,
                            'fill-extrusion-vertical-gradient': true
                        }
                    });
                    
                    console.log('🏢 [3D CLIENT] Added buildings extrusion layer with', buildingsGeoJSON.features.length, 'buildings');
                    console.log('🏢 [3D CLIENT] Building features:', buildingsGeoJSON.features.map(f => ({
                        name: f.properties.name,
                        height: f.properties.height,
                        coordinates: f.geometry.coordinates[0].length + ' points'
                    })));
                }
            }
            
            // Process parcels using Mapbox fill-extrusion (same data as 2D rendering)
            if (object.parcels && object.parcels.length > 0) {
                console.log('🏢 [3D CLIENT] Processing parcels with Mapbox fill-extrusion (same data as 2D)...');
                console.log('🏢 [3D CLIENT] Using same parcel data as 2D MapResource component');
                console.log('🏢 [3D CLIENT] First parcel structure:', object.parcels[0]);
                console.log('🏢 [3D CLIENT] All parcel keys:', Object.keys(object.parcels[0]));
                console.log('🏢 [3D CLIENT] Parcel geometry field:', object.parcels[0].geometry);
                console.log('🏢 [3D CLIENT] Parcel shape field:', object.parcels[0].shape);
                console.log('🏢 [3D CLIENT] Parcel boundary field:', object.parcels[0].boundary);
                console.log('🏢 [3D CLIENT] Parcel polygon field:', object.parcels[0].polygon);
                
                // Create GeoJSON for all parcels
                const parcelsGeoJSON = {
                    type: 'FeatureCollection' as const,
                    features: object.parcels
                        .map((parcel: any, index: number) => {
                            console.log('🏢 [3D CLIENT] Processing parcel:', {
                                index,
                                hasGeometry: !!parcel.geometry,
                                geometryType: parcel.geometry?.type,
                                geometryCoordinates: parcel.geometry?.coordinates,
                                geometryCoordinatesLength: parcel.geometry?.coordinates?.length,
                                parcelKeys: Object.keys(parcel),
                                fullParcel: JSON.stringify(parcel, null, 2)
                            });
                            
                            // Check if parcel has coordinates in various possible locations
                            let coordinates = null;
                            
                            // First, try the same structure as the 2D rendering (MainShapeFittingAlgorithm)
                            if (parcel.geometry && parcel.geometry.coordinates && Array.isArray(parcel.geometry.coordinates)) {
                                console.log('🏢 [3D CLIENT] Parcel geometry structure:', {
                                    type: parcel.geometry.type,
                                    coordinatesLength: parcel.geometry.coordinates.length,
                                    firstCoordinates: parcel.geometry.coordinates[0],
                                    firstCoordinatesLength: parcel.geometry.coordinates[0]?.length
                                });
                                
                                // Handle GeoJSON Polygon structure: coordinates[0] is the outer ring
                                if (parcel.geometry.type === 'Polygon' && parcel.geometry.coordinates[0]) {
                                    coordinates = [parcel.geometry.coordinates[0]]; // Wrap in array for GeoJSON format
                                    console.log('🏢 [3D CLIENT] Found Polygon coordinates in parcel.geometry.coordinates[0]');
                                } else if (parcel.geometry.type === 'MultiPolygon' && parcel.geometry.coordinates[0] && parcel.geometry.coordinates[0][0]) {
                                    coordinates = [parcel.geometry.coordinates[0][0]]; // First polygon of MultiPolygon
                                    console.log('🏢 [3D CLIENT] Found MultiPolygon coordinates in parcel.geometry.coordinates[0][0]');
                                } else {
                                    coordinates = parcel.geometry.coordinates;
                                    console.log('🏢 [3D CLIENT] Found coordinates in parcel.geometry.coordinates (direct)');
                                }
                            } else if (parcel.coordinates && Array.isArray(parcel.coordinates)) {
                                coordinates = parcel.coordinates;
                                console.log('🏢 [3D CLIENT] Found coordinates in parcel.coordinates');
                            } else if (parcel.shape && parcel.shape.coordinates && Array.isArray(parcel.shape.coordinates)) {
                                coordinates = parcel.shape.coordinates;
                                console.log('🏢 [3D CLIENT] Found coordinates in parcel.shape.coordinates');
                            } else if (parcel.boundary && parcel.boundary.coordinates && Array.isArray(parcel.boundary.coordinates)) {
                                coordinates = parcel.boundary.coordinates;
                                console.log('🏢 [3D CLIENT] Found coordinates in parcel.boundary.coordinates');
                            } else if (parcel.polygon && parcel.polygon.coordinates && Array.isArray(parcel.polygon.coordinates)) {
                                coordinates = parcel.polygon.coordinates;
                                console.log('🏢 [3D CLIENT] Found coordinates in parcel.polygon.coordinates');
                            } else if (parcel.geo && parcel.geo.coordinates && Array.isArray(parcel.geo.coordinates)) {
                                coordinates = parcel.geo.coordinates;
                                console.log('🏢 [3D CLIENT] Found coordinates in parcel.geo.coordinates');
                            } else if (parcel.geoJson && parcel.geoJson.coordinates && Array.isArray(parcel.geoJson.coordinates)) {
                                coordinates = parcel.geoJson.coordinates;
                                console.log('🏢 [3D CLIENT] Found coordinates in parcel.geoJson.coordinates');
                            } else if (parcel.geometry && parcel.geometry.geometry && parcel.geometry.geometry.coordinates && Array.isArray(parcel.geometry.geometry.coordinates)) {
                                coordinates = parcel.geometry.geometry.coordinates;
                                console.log('🏢 [3D CLIENT] Found coordinates in parcel.geometry.geometry.coordinates');
                            } else {
                                // Try to find coordinates in the same way as the 2D rendering
                                console.log('🏢 [3D CLIENT] Trying to find coordinates like 2D rendering...');
                                console.log('🏢 [3D CLIENT] Parcel structure analysis:', {
                                    hasGeometry: !!parcel.geometry,
                                    geometryType: parcel.geometry?.type,
                                    geometryKeys: parcel.geometry ? Object.keys(parcel.geometry) : [],
                                    geometryCoordinates: parcel.geometry?.coordinates,
                                    geometryCoordinatesType: typeof parcel.geometry?.coordinates,
                                    geometryCoordinatesIsArray: Array.isArray(parcel.geometry?.coordinates),
                                    geometryCoordinatesLength: parcel.geometry?.coordinates?.length,
                                    firstCoordinate: parcel.geometry?.coordinates?.[0],
                                    firstCoordinateType: typeof parcel.geometry?.coordinates?.[0],
                                    firstCoordinateIsArray: Array.isArray(parcel.geometry?.coordinates?.[0])
                                });
                            }
                            
                            // If no polygon coordinates found, create a simple square around the center point
                            if (!coordinates && parcel.centerLat && parcel.centerLon) {
                                console.log('🏢 [3D CLIENT] Creating square parcel around center point:', {
                                    centerLat: parcel.centerLat,
                                    centerLon: parcel.centerLon
                                });
                                
                                // Create a small square around the center point (about 50m x 50m)
                                const latOffset = 0.0005; // ~50m
                                const lonOffset = 0.0005; // ~50m
                                
                                coordinates = [[
                                    [parcel.centerLon - lonOffset, parcel.centerLat - latOffset],
                                    [parcel.centerLon + lonOffset, parcel.centerLat - latOffset],
                                    [parcel.centerLon + lonOffset, parcel.centerLat + latOffset],
                                    [parcel.centerLon - lonOffset, parcel.centerLat + latOffset],
                                    [parcel.centerLon - lonOffset, parcel.centerLat - latOffset] // Close the polygon
                                ]];
                                
                                console.log('🏢 [3D CLIENT] Generated square coordinates:', coordinates);
                            }
                            
                            if (coordinates) {
                                const height = 0.1; // Low height for parcels (10cm)
                                
                                console.log('🏢 [3D CLIENT] Parcel coordinates found:', {
                                    coordinates: coordinates,
                                    height: height
                                });
                                
                                // Validate coordinates - check if they're reasonable
                                if (coordinates && coordinates[0] && Array.isArray(coordinates[0])) {
                                    const firstRing = coordinates[0];
                                    const validCoords = firstRing.filter((coord: any) => {
                                        if (Array.isArray(coord) && coord.length >= 2) {
                                            const [lon, lat] = coord;
                                            // Check if coordinates are reasonable (within a reasonable range)
                                            const isReasonable = lon > 8.8 && lon < 8.9 && lat > 50.1 && lat < 50.3;
                                            if (!isReasonable) {
                                                console.warn('🏢 [3D CLIENT] Invalid parcel coordinate detected:', coord);
                                            }
                                            return isReasonable;
                                        }
                                        return false;
                                    });
                                    
                                    if (validCoords.length < 3) {
                                        console.warn('🏢 [3D CLIENT] Parcel has too few valid coordinates:', validCoords.length);
                                        return null;
                                    }
                                    
                                    console.log('🏢 [3D CLIENT] Parcel has', validCoords.length, 'valid coordinates out of', firstRing.length);
                                    
                                    // Ensure polygon is properly closed
                                    const closedCoords = [...validCoords];
                                    if (closedCoords.length > 0) {
                                        const first = closedCoords[0];
                                        const last = closedCoords[closedCoords.length - 1];
                                        if (first[0] !== last[0] || first[1] !== last[1]) {
                                            closedCoords.push([first[0], first[1]]);
                                            console.log('🏢 [3D CLIENT] Closed polygon for parcel', index);
                                        }
                                    }
                                    
                                    return {
                                        type: 'Feature' as const,
                                        geometry: {
                                            type: 'Polygon' as const,
                                            coordinates: [closedCoords] // Use closed coordinates
                                        },
                                        properties: {
                                            height: height,
                                            color: '#90EE90', // Light green for parcels
                                            name: `Parcel ${index}`
                                        }
                                    };
                                }
                            } else {
                                console.warn('🏢 [3D CLIENT] No coordinates found for parcel', index);
                            }
                            return null;
                        })
                        .filter((feature): feature is NonNullable<typeof feature> => feature !== null)
                };
                
                // Add parcels source and layer
                if (parcelsGeoJSON.features.length > 0) {
                    map.addSource('parcels-extrusion', {
                        type: 'geojson',
                        data: parcelsGeoJSON
                    });
                    
                    map.addLayer({
                        id: 'parcels-extrusion-layer',
                        type: 'fill-extrusion',
                        source: 'parcels-extrusion',
                        paint: {
                            'fill-extrusion-color': ['get', 'color'],
                            'fill-extrusion-height': ['get', 'height'],
                            'fill-extrusion-base': 0,
                            'fill-extrusion-opacity': 0.6
                        }
                    });
                    
                    console.log('🏢 [3D CLIENT] Added parcels extrusion layer with', parcelsGeoJSON.features.length, 'parcels');
                } else {
                    console.warn('🏢 [3D CLIENT] No valid parcels found to render');
                }
            }
        });

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
            }
        };
    }, [object]);

    // Note: Old Three.js extrusion functions are no longer used - now using Mapbox fill-extrusion layers
    /*
    const createExtrudedBuilding = (coordinates: any, height: number, name: string, centerLat: number, centerLon: number) => {
        try {
            console.log('🏢 [3D EXTRUDE] Creating extruded building:', { name, height, coordinates });
            console.log('🏢 [3D EXTRUDE] Coordinate structure:', {
                coordinates: coordinates,
                firstCoord: coordinates[0],
                firstCoordType: typeof coordinates[0],
                firstCoordIsArray: Array.isArray(coordinates[0]),
                fullCoordinates: JSON.stringify(coordinates, null, 2)
            });
            
            // Handle different coordinate formats (Polygon vs MultiPolygon)
            let polygonCoords = coordinates;
            if (coordinates[0] && Array.isArray(coordinates[0])) {
                // MultiPolygon - take first polygon
                polygonCoords = coordinates[0];
            }
            
            // If coordinates are nested arrays, we need to handle them properly
            // The structure is: [[[lon, lat], [lon, lat], ...]]
            if (Array.isArray(polygonCoords) && polygonCoords.length > 0) {
                if (Array.isArray(polygonCoords[0]) && polygonCoords[0].length > 0) {
                    // Check if the first element is a coordinate pair
                    if (Array.isArray(polygonCoords[0][0])) {
                        // This is [[[lon, lat], [lon, lat], ...]] - take the first ring
                        polygonCoords = polygonCoords[0];
                    }
                }
            }
            
            console.log('🏢 [3D EXTRUDE] Processed polygon coordinates:', polygonCoords);
            console.log('🏢 [3D EXTRUDE] First coordinate:', polygonCoords[0]);
            console.log('🏢 [3D EXTRUDE] First coordinate type:', typeof polygonCoords[0]);
            console.log('🏢 [3D EXTRUDE] First coordinate is array:', Array.isArray(polygonCoords[0]));
            
            // Convert geographic coordinates to local coordinates
            const localCoords = polygonCoords.map((coord: any) => {
                // Handle different coordinate formats
                let lon, lat;
                if (Array.isArray(coord) && coord.length >= 2) {
                    [lon, lat] = coord;
                } else if (coord && typeof coord === 'object' && coord.lon !== undefined && coord.lat !== undefined) {
                    lon = coord.lon;
                    lat = coord.lat;
                } else if (coord && typeof coord === 'object' && coord.longitude !== undefined && coord.latitude !== undefined) {
                    lon = coord.longitude;
                    lat = coord.latitude;
                } else {
                    console.warn('🏢 [3D EXTRUDE] Invalid coordinate format:', coord);
                    return [0, 0];
                }
                
                const x = (lon - centerLon) * 111320 * Math.cos(centerLat * Math.PI / 180);
                const y = (lat - centerLat) * 111320;
                return [x, y];
            });
            
            // Create shape from coordinates
            const shape = new THREE.Shape();
            shape.moveTo(localCoords[0][0], localCoords[0][1]);
            for (let i = 1; i < localCoords.length; i++) {
                shape.lineTo(localCoords[i][0], localCoords[i][1]);
            }
            shape.closePath();
            
            // Extrude the shape
            const extrudeSettings = {
                depth: height,
                bevelEnabled: false
            };
            
            const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
            const material = new THREE.MeshPhongMaterial({ 
                color: 0x888888,
                transparent: true,
                opacity: 0.8
            });
            
            const mesh = new THREE.Mesh(geometry, material);
            
            // Create threebox object
            const object3D = (window as any).tb.Object3D({ obj: mesh });
            object3D.setCoords([centerLon, centerLat]);
            (window as any).tb.add(object3D);
            
            console.log('🏢 [3D EXTRUDE] Building extruded successfully:', name);
            console.log('🏢 [3D EXTRUDE] Object3D created:', object3D);
            console.log('🏢 [3D EXTRUDE] Threebox scene children count:', (window as any).tb.scene.children.length);
            
        } catch (error) {
            console.error('🏢 [3D EXTRUDE] Error creating extruded building:', error);
        }
    };

    // Helper method to create extruded parcel from polygon coordinates
    const createExtrudedParcel = (coordinates: any, height: number, name: string, centerLat: number, centerLon: number) => {
        try {
            console.log('🏢 [3D EXTRUDE] Creating extruded parcel:', { name, height, coordinates });
            
            // Handle different coordinate formats (Polygon vs MultiPolygon)
            let polygonCoords = coordinates;
            if (coordinates[0] && Array.isArray(coordinates[0])) {
                // MultiPolygon - take first polygon
                polygonCoords = coordinates[0];
            }
            
            // If coordinates are nested arrays, we need to handle them properly
            // The structure is: [[[lon, lat], [lon, lat], ...]]
            if (Array.isArray(polygonCoords) && polygonCoords.length > 0) {
                if (Array.isArray(polygonCoords[0]) && polygonCoords[0].length > 0) {
                    // Check if the first element is a coordinate pair
                    if (Array.isArray(polygonCoords[0][0])) {
                        // This is [[[lon, lat], [lon, lat], ...]] - take the first ring
                        polygonCoords = polygonCoords[0];
                    }
                }
            }
            
            console.log('🏢 [3D EXTRUDE] Processed polygon coordinates:', polygonCoords);
            console.log('🏢 [3D EXTRUDE] First coordinate:', polygonCoords[0]);
            console.log('🏢 [3D EXTRUDE] First coordinate type:', typeof polygonCoords[0]);
            console.log('🏢 [3D EXTRUDE] First coordinate is array:', Array.isArray(polygonCoords[0]));
            
            // Convert geographic coordinates to local coordinates
            const localCoords = polygonCoords.map((coord: any) => {
                // Handle different coordinate formats
                let lon, lat;
                if (Array.isArray(coord) && coord.length >= 2) {
                    [lon, lat] = coord;
                } else if (coord && typeof coord === 'object' && coord.lon !== undefined && coord.lat !== undefined) {
                    lon = coord.lon;
                    lat = coord.lat;
                } else if (coord && typeof coord === 'object' && coord.longitude !== undefined && coord.latitude !== undefined) {
                    lon = coord.longitude;
                    lat = coord.latitude;
                } else {
                    console.warn('🏢 [3D EXTRUDE] Invalid coordinate format:', coord);
                    return [0, 0];
                }
                
                const x = (lon - centerLon) * 111320 * Math.cos(centerLat * Math.PI / 180);
                const y = (lat - centerLat) * 111320;
                return [x, y];
            });
            
            // Create shape from coordinates
            const shape = new THREE.Shape();
            shape.moveTo(localCoords[0][0], localCoords[0][1]);
            for (let i = 1; i < localCoords.length; i++) {
                shape.lineTo(localCoords[i][0], localCoords[i][1]);
            }
            shape.closePath();
            
            // Extrude the shape
            const extrudeSettings = {
                depth: height,
                bevelEnabled: false
            };
            
            const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
            const material = new THREE.MeshPhongMaterial({ 
                color: 0x90EE90, // Light green for parcels
                transparent: true,
                opacity: 0.6
            });
            
            const mesh = new THREE.Mesh(geometry, material);
            
            // Create threebox object
            const object3D = (window as any).tb.Object3D({ obj: mesh });
            object3D.setCoords([centerLon, centerLat]);
            (window as any).tb.add(object3D);
            
            console.log('🏢 [3D EXTRUDE] Parcel extruded successfully:', name);
            
        } catch (error) {
            console.error('🏢 [3D EXTRUDE] Error creating extruded parcel:', error);
        }
    };
    */

    return (
        <div className='positionRelative'>
            <div id="map" className="flexGrow1" ref={mapContainerRef} style={{ height: '400px', width: '100%' }}></div>
        </div>
    );
};
